import { memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";


const MemoizedMarkdownBlock = memo(
  ({ content, variant = "chat" }) => {
    const colorScheme = {
      chat: {
        text: "white",
        heading: "white",
        link: "#60a5fa",
        linkHover: "#93c5fd",
        code: "white",
        codeBg: "rgba(255, 255, 255, 0.1)",
        blockquote: "rgba(255, 255, 255, 0.9)",
        blockquoteBorder: "rgba(255, 255, 255, 0.3)",
      },
      profile: {
        text: "#374151",
        heading: "#1f2937",
        link: "#3b82f6",
        linkHover: "#2563eb",
        code: "#1f2937",
        codeBg: "rgba(0, 0, 0, 0.05)",
        blockquote: "#6b7280",
        blockquoteBorder: "rgba(0, 0, 0, 0.1)",
      },
    };

    const colors = colorScheme[variant];

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        // ✅ FIXED: Removed rehypePlugins with rehypeRaw
        // This prevents raw HTML from being rendered and executed
        components={{
          strong: ({ children, ...props }) => (
            <strong
              {...props}
              style={{ color: colors.heading, fontWeight: "600" }}
            >
              {children}
            </strong>
          ),
          p: ({ children, ...props }) => (
            <p
              {...props}
              style={{
                color: colors.text,
                marginBottom: "0.75rem",
                lineHeight: "1.7",
                fontSize: "0.8rem",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                wordBreak: "break-word",
                hyphens: "auto",
                whiteSpace: "pre-wrap",
              }}
            >
              {children}
            </p>
          ),
          ul: ({ children, ...props }) => (
            <ul
              {...props}
              style={{
                color: colors.text,
                margin: "0.75rem 0",
                paddingLeft: "1.25rem",
                listStyleType: "disc",
                listStylePosition: "outside",
              }}
            >
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol
              {...props}
              style={{
                color: colors.text,
                margin: "1rem 0",
                paddingLeft: "1.5rem",
              }}
            >
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li
              {...props}
              style={{
                color: colors.text,
                marginBottom: "0.5rem",
                lineHeight: "1.6",
                fontSize: "0.8rem",
                paddingLeft: "0.25rem",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {children}
            </li>
          ),
          h1: ({ children, ...props }) => (
            <h1
              {...props}
              style={{
                color: colors.heading,
                marginTop: "1.5rem",
                marginBottom: "1rem",
                fontSize: "1.2rem",
                fontWeight: "600",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2
              {...props}
              style={{
                color: colors.heading,
                marginTop: "1.5rem",
                marginBottom: "1rem",
                fontSize: "1.1rem",
                fontWeight: "600",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3
              {...props}
              style={{
                color: colors.heading,
                marginTop: "1.5rem",
                marginBottom: "1rem",
                fontSize: "1rem",
                fontWeight: "600",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {children}
            </h3>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote
              {...props}
              style={{
                color: colors.blockquote,
                borderLeft: `3px solid ${colors.blockquoteBorder}`,
                paddingLeft: "1rem",
                margin: "1rem 0",
                fontStyle: "italic",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {children}
            </blockquote>
          ),
          code: ({ children, ...props }) => (
            <code
              {...props}
              style={{
                color: colors.code,
                backgroundColor: colors.codeBg,
                padding: "0.2rem 0.4rem",
                borderRadius: "4px",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                wordBreak: "break-all",
                whiteSpace: "pre-wrap",
                fontSize: "0.75rem",
              }}
            >
              {children}
            </code>
          ),
          pre: ({ children, ...props }) => (
            <pre
              {...props}
              style={{
                backgroundColor: colors.codeBg,
                padding: "0.3rem",
                borderRadius: "6px",
                margin: "1rem 0",
                lineHeight: "1.4rem",
                fontSize: "0.75rem",
              }}
            >
              {children}
            </pre>
          ),
          a: ({ children, href, ...props }) => (
            <a
              {...props}
              href={href}
              // ✅ SECURITY: Prevent javascript: and data: URLs
              onClick={(e) => {
                if (href?.startsWith('javascript:') || href?.startsWith('data:')) {
                  e.preventDefault();
                  return false;
                }
              }}
              // ✅ SECURITY: Prevent target="_blank" attacks
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: colors.link,
                textDecoration: "underline",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
              onMouseEnter={(e) => {
                e.target.style.color = colors.linkHover;
              }}
              onMouseLeave={(e) => {
                e.target.style.color = colors.link;
              }}
            >
              {children}
            </a>
          ),
          // ✅ SECURITY: Sanitize image sources
          img: ({ src, alt, ...props }) => {
            // Only allow http/https images, block data: and javascript:
            const isSafeUrl = src?.startsWith('http://') || src?.startsWith('https://');
            
            if (!isSafeUrl) {
              return null; // Don't render unsafe images
            }
            
            return (
              <img
                {...props}
                src={src}
                alt={alt || ''}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  margin: '0.5rem 0',
                }}
              />
            );
          },
          table: ({ children, ...props }) => (
            <div style={{ 
              overflowX: "auto", 
              overflowY: "visible",
              margin: "1rem 0",
              maxWidth: "100%",
              scrollbarWidth: "thin",
              scrollbarColor: variant === "chat" 
                ? "rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.1)",
            }}>
              <table
                {...props}
                style={{
                  width: "auto",
                  borderCollapse: "collapse",
                  backgroundColor:
                    variant === "chat"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                  borderRadius: "6px",
                  overflow: "hidden",
                  tableLayout: "auto",
                }}
              >
                {children}
              </table>
            </div>
          ),
          thead: ({ children, ...props }) => (
            <thead
              {...props}
              style={{
                backgroundColor:
                  variant === "chat"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.05)",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              {children}
            </thead>
          ),
          tbody: ({ children, ...props }) => (
            <tbody {...props}>{children}</tbody>
          ),
          tr: ({ children, ...props }) => (
            <tr
              {...props}
              style={{
                borderBottom: `1px solid ${
                  variant === "chat"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)"
                }`,
              }}
            >
              {children}
            </tr>
          ),
          th: ({ children, ...props }) => (
            <th
              {...props}
              style={{
                color: colors.heading,
                padding: "0.5rem 0.75rem",
                textAlign: "left",
                fontWeight: "600",
                fontSize: "0.85rem",
                borderBottom: `2px solid ${
                  variant === "chat"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(0, 0, 0, 0.2)"
                }`,
                whiteSpace: "nowrap",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                minWidth: "40px",
              }}
            >
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td
              {...props}
              style={{
                color: colors.text,
                padding: "0.5rem 0.75rem",
                fontSize: "0.85rem",
                lineHeight: "1.5",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
                minWidth: "60px",
              }}
            >
              {children}
            </td>
          ),
        }}
      >
        {content || ""}
      </ReactMarkdown>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.content === nextProps.content &&
      prevProps.variant === nextProps.variant
    );
  }
);

MemoizedMarkdownBlock.displayName = "MemoizedMarkdownBlock";

export const MemoizedMarkdown = memo(({ content, id, variant = "chat" }) => {
  return (
    <MemoizedMarkdownBlock content={content || ""} variant={variant} key={id} />
  );
});

MemoizedMarkdown.displayName = "MemoizedMarkdown";